import React, { createContext, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Skeleton } from '../../components/ui/skeleton'

import {
  initialUserSignInFormData,
  initialBussinessSignInFormData,
  initialBussinessSignUpFormData,
  initialUserSignUpFormData,
  initialForgotPasswordFormdata,
  bussinessSignUpFormControls,
  initialBussinessSignUpFormControls,
} from '../../config'
import {
  businessLoginService,
  businessRegisterService,
  checkAuthService,
  forgotPasswordService,
  userLoginService,
  userRegisterService,
  verifyOtp,
} from '../../services'

import logger from '@/utils/logger'

export const AuthContext = createContext(null)

function AuthProvider({ children }) {
  const [loading, setLoading] = useState(false)
  const [auth, setAuth] = useState({
    authenticate: false,
    user: null,
  })
  const [bussinessSignUpFormControls, setBussinessSignUpFormControls] = useState(
    initialBussinessSignUpFormControls
  )

  // user and bussiness form data state
  const [userSignInFormData, setUserSignInFormData] = useState(initialUserSignInFormData)
  const [bussinessSignInFormData, setBussinessSignInFormData] = useState(
    initialBussinessSignInFormData
  )

  const [userSignUpFormData, setUserSignUpFormData] = useState(initialUserSignUpFormData)

  const [bussinessSignUpFormData, setBussinessSignUpFormData] = useState(
    initialBussinessSignUpFormData
  )

  const [forgotPasswordFormdata, setForgotPasswordFormdata] = useState(
    initialForgotPasswordFormdata
  )

  // user and bussiness form loading state
  const [userSignInLoading, setUserSignInLoading] = useState(false)
  const [bussinessSignInLoading, setBussinessSignInLoading] = useState(false)

  const [userSignUpLoading, setUserSignUpLoading] = useState(false)

  const [bussinessSignUpLoading, setBussinessSignUpLoading] = useState(false)

  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false)

  // modal states
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)
  const [modalContent, setModalContent] = useState(null)

  const [accountAlreadyCreated, setAccountAlreadyCreated] = useState(false)

  // active tab of the login modal
  const [activeTab, setActiveTab] = useState('user')
  const [otp, setOtp] = useState(['', '', '', ''])

  // ===================================================== || Global functions || ===============================================================

  const handleSignInUser = async (e) => {
    e.preventDefault()
    setUserSignInLoading(true)
    try {
      const data = await userLoginService(userSignInFormData)

      if (data.status === 200) {
        toast.success(data?.data?.message)

        if (data.data?.token) {
          localStorage.setItem('accessToken', JSON.stringify(data.data.token))
          setAuth({
            authenticate: true,
            user: data.data.data,
          })
          setIsLoginModalOpen(false)
          setActiveTab('user')
          window.location.href = '/user'
        } else {
          setActiveTab('otp-verify')
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message)
    } finally {
      setUserSignInLoading(false)
    }
  }

  const handleSignUpUser = async (e) => {
    e.preventDefault()
    setUserSignUpLoading(true)
    try {
      const data = await userRegisterService(userSignUpFormData)
      if (data.status === 200) {
        toast.success(data?.message)
        setUserSignUpFormData(initialUserSignUpFormData)
        setIsLoginModalOpen(false)
      }
    } catch (error) {
      toast.error(error?.response?.data?.message)
    } finally {
      setUserSignUpLoading(false)
    }
  }

  const handleSignInBussiness = async (e) => {
    e.preventDefault()
    setBussinessSignInLoading(true)
    try {
      const { data } = await businessLoginService(bussinessSignInFormData)

      if (data.status === 200) {
        localStorage.setItem('accessToken', JSON.stringify(data.token))
        setAuth({
          authenticate: true,
          user: data.data,
        })
        toast.success(data?.message)
        setBussinessSignInFormData(initialBussinessSignInFormData)
        setIsLoginModalOpen(false)
        setActiveTab('user')

        if (
          data.data?.subscription &&
          !data.data?.subscription?.cancelAtPeriodEnd &&
          data.data?.subscription?.status === 'active'
        ) {
          window.location.href = '/business/dashboard'
        }
      } else {
        setAuth({
          authenticate: false,
          user: null,
        })
      }
    } catch (error) {
      toast.error(error?.response?.data?.message)
    } finally {
      setBussinessSignInLoading(false)
    }
  }
  const {
    address,
    business_name,
    business_type,
    country_code,
    email,
    mobile,
    acceptTerms,
    ...rest // should be an array of File objects (e.g., from <input type="file" multiple />)
  } = bussinessSignUpFormData

  const formDataObject = {
    address,
    business_name,
    business_type,
    country_code,
    email,
    mobile,
    acceptTerms,
    documents: Object.entries(rest).map(([key, value]) => ({
      key,
      value,
    })),
  }

  const handleSignUpBussiness = async (e) => {
    e.preventDefault()
    setBussinessSignUpLoading(true)
    try {
      const response = await businessRegisterService(formDataObject)
      if (response.status === 200) {
        toast.success(response.data?.message)
        setBussinessSignUpFormData(initialBussinessSignUpFormData)
        setBussinessSignUpFormControls(initialBussinessSignUpFormControls)
        setIsLoginModalOpen(false)
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Something went wrong')
    } finally {
      setBussinessSignUpLoading(false)
    }
  }

  const handleVerifyOtp = async () => {
    const combinedOtp = otp.join('')
    const verifyOtpFormData = {
      mobile: userSignInFormData.mobile,
      otp: combinedOtp,
      country_code: userSignInFormData.country_code,
    }

    try {
      const { data } = await verifyOtp(verifyOtpFormData)

      if (data.status === 200) {
        // Save token securely

        localStorage.setItem('accessToken', JSON.stringify(data.token))
        setAuth({
          authenticate: true,
          user: data.data,
        })

        toast.success(data?.message)
        setIsLoginModalOpen(false)
        setActiveTab('user')
        window.location.href = '/user'
      } else {
        setAuth({
          authenticate: false,
          user: null,
        })
      }
    } catch (error) {
      toast.error(error?.response?.data?.message)
    }
  }

  // check auth user on the page reload
  const checkAuthUser = async () => {
    try {
      const data = await checkAuthService()

      if (data.status === 200) {
        setAuth({
          authenticate: true,
          user: data.data,
        })
        setLoading(false)
        setIsLoginModalOpen(false)
      } else {
        setAuth({
          authenticate: false,
          user: null,
        })
        setLoading(false)
        setIsLoginModalOpen(false)
      }
    } catch (error) {
      logger.error(error)
      if (!(error?.response?.data?.status === 200)) {
        setAuth({
          authenticate: false,
          user: null,
        })
        setLoading(false)
        setIsLoginModalOpen(false)
      }
    }
  }

  useEffect(() => {
    setLoading(true) // ✅ add this line
    checkAuthUser()
  }, [])

  function resetCredentials() {
    // Clear any saved return URL when logging out
    sessionStorage.removeItem('returnUrl')

    // Reset the auth state
    setAuth({
      authenticate: false,
      user: null,
    })

    // Optional: Redirect to home page
    window.location.href = '/'
  }

  const navigateCMSpage = (urlkey) => {
    const atag = document.createElement('a')
    atag.href = `${import.meta.env.VITE_ADMIN_BASE_URL}/cms/` + urlkey // todo -> .env url of admin panel;
    atag.target = '_blank'
    atag.click()
  }

  const handleForgotPasswordButtonClick = async () => {
    setForgotPasswordLoading(true)
    try {
      const data = await forgotPasswordService(forgotPasswordFormdata)
      if (data.status === 200) {
        toast.success(data?.data?.message)
        setForgotPasswordFormdata(initialForgotPasswordFormdata)
        setActiveTab('businesses')
      }
    } catch (error) {
      toast.error(error?.response?.data?.message)
    } finally {
      setForgotPasswordLoading(false)
    }
  }

  logger.log(auth)

  return (
    <AuthContext.Provider
      value={{
        // auth
        auth,
        loading, // ✅ add this line
        setAuth,

        // modal state
        isLoginModalOpen,
        setIsLoginModalOpen,
        modalContent,
        setModalContent,
        accountAlreadyCreated,
        setAccountAlreadyCreated,

        // activetab
        activeTab,
        setActiveTab,
        otp,
        setOtp,

        // formdata state
        userSignInFormData,
        setUserSignInFormData,
        bussinessSignInFormData,
        setBussinessSignInFormData,
        userSignUpFormData,
        setUserSignUpFormData,
        bussinessSignUpFormData,
        setBussinessSignUpFormData,
        forgotPasswordFormdata,
        setForgotPasswordFormdata,
        bussinessSignUpFormControls,
        setBussinessSignUpFormControls,

        // loading state
        userSignInLoading,
        bussinessSignInLoading,
        userSignUpLoading,
        bussinessSignUpLoading,
        forgotPasswordLoading,

        // functions
        handleSignInBussiness,
        handleSignInUser,
        handleSignUpBussiness,
        handleSignUpUser,
        handleVerifyOtp,
        resetCredentials,
        checkAuthUser,
        handleForgotPasswordButtonClick,
        navigateCMSpage,
      }}
    >
      {loading ? <Skeleton /> : children}
    </AuthContext.Provider>
  )
}

export default AuthProvider
